import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import StatusBadge from '@/components/StatusBadge';
import { TableCell, TableRow } from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import { BadgeCheck, CheckCircle2, FileText, Loader2, PauseCircle, XCircle } from 'lucide-react';
import PartnerDocumentsDialog from '@/components/PartnerDocumentsDialog';

function VerifiedCell({ verified }) {
  if (verified) {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700" data-testid="verified-yes">
        <BadgeCheck className="h-4 w-4" /> Yes
      </span>
    );
  }
  return <span className="text-slate-400 text-sm" data-testid="verified-no">No</span>;
}

function ActionButton({ icon: Icon, label, color, busy, disabled, onClick, testId }) {
  return (
    <Button
      size="sm"
      variant="outline"
      className={color}
      disabled={disabled || busy}
      onClick={onClick}
      data-testid={testId}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
      <span className="ml-1">{label}</span>
    </Button>
  );
}

export default function PartnerRow({ partner, actionLoading, onAction }) {
  const isApproved = partner.status === 'approved';
  const isRejected = partner.status === 'rejected';
  const isSuspended = partner.status === 'suspended';
  const anyAction = !!actionLoading;
  const [docsOpen, setDocsOpen] = useState(false);

  return (
    <TableRow data-testid={`partner-row-${partner.id}`}>
      <TableCell className="font-medium text-slate-900">{partner.business_name}</TableCell>
      <TableCell>{partner.category}</TableCell>
      <TableCell className="font-mono text-sm">{partner.base_postcode}</TableCell>
      <TableCell><StatusBadge value={partner.status} /></TableCell>
      <TableCell><VerifiedCell verified={partner.verified_badge} /></TableCell>
      <TableCell className="text-sm text-slate-600">{formatDate(partner.created_at)}</TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-2">
          <ActionButton
            icon={FileText}
            label="Docs"
            color="border-slate-200 text-slate-700 hover:bg-slate-50"
            busy={false}
            disabled={false}
            onClick={() => setDocsOpen(true)}
            testId={`btn-docs-${partner.id}`}
          />
          <ActionButton
            icon={CheckCircle2}
            label="Approve"
            color="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            busy={actionLoading === 'approve'}
            disabled={anyAction || isApproved}
            onClick={() => onAction(partner.id, 'approve')}
            testId={`btn-approve-${partner.id}`}
          />
          <ActionButton
            icon={XCircle}
            label="Reject"
            color="border-rose-200 text-rose-700 hover:bg-rose-50"
            busy={actionLoading === 'reject'}
            disabled={anyAction || isRejected}
            onClick={() => onAction(partner.id, 'reject')}
            testId={`btn-reject-${partner.id}`}
          />
          <ActionButton
            icon={PauseCircle}
            label="Suspend"
            color="border-slate-300 text-slate-700 hover:bg-slate-100"
            busy={actionLoading === 'suspend'}
            disabled={anyAction || isSuspended}
            onClick={() => onAction(partner.id, 'suspend')}
            testId={`btn-suspend-${partner.id}`}
          />
        </div>
        <PartnerDocumentsDialog
          open={docsOpen}
          onOpenChange={setDocsOpen}
          partner={partner}
        />
      </TableCell>
    </TableRow>
  );
}
